import { signOut } from '@/lib/auth';
import { esc } from '@/lib/dom';
import { navIcon } from '@/lib/nav-icons';
import { getMyProfile, logoUrl } from '@/lib/data/branding';
import { companyName, tagline, logoPath, photoPath } from '@/lib/branding';
import { brandLockup } from '@/lib/brand-lockup';
import { ensureBrandFont } from '@/lib/brand-font-loader';

export type GlobalKey = 'projects' | 'library' | 'contacts' | 'settings';
export type ProjectSectionKey =
  | 'dashboard' | 'rooms' | 'exterior' | 'schedule' | 'punch' | 'budget' | 'specs' | 'files' | 'contacts'
  | 'notes' | 'change_orders' | 'questionnaire' | 'export';

export type ShellContext =
  | { kind: 'global'; active: GlobalKey }
  | {
      kind: 'project';
      projectId: string;
      projectName: string;
      activeSection: ProjectSectionKey;
      activeRoomName?: string;
    };

export interface ShellHandle {
  main: HTMLElement;
  setContext(ctx: ShellContext): void;
}

// Section keys stay stable so existing URLs keep working — only labels change.
const PROJECT_HUB: { key: ProjectSectionKey; label: string }[] = [
  { key: 'dashboard',     label: 'Dashboard' },
  { key: 'rooms',         label: 'Interior' },
  { key: 'exterior',      label: 'Exterior' },
  { key: 'schedule',      label: 'Install Schedule' },
  { key: 'punch',         label: 'Punch List' },
  { key: 'budget',        label: 'Budget' },
  { key: 'specs',         label: 'Materials' },
  { key: 'files',         label: 'Files & Docs' },
  { key: 'contacts',      label: 'Contacts' },
  { key: 'notes',         label: 'Notes' },
  { key: 'change_orders', label: 'Change Orders' },
];

const PROJECT_TOOLS: { key: ProjectSectionKey; label: string }[] = [
  { key: 'questionnaire', label: 'Style Questionnaire' },
  { key: 'export',        label: 'Export & Print' },
];

export function renderShell(root: HTMLElement, userEmail: string, firmName?: string | null): ShellHandle {
  // The firm's own name until its saved branding loads (below) — never a
  // built-in firm name (Phase 27).
  const initialName = companyName(null, firmName);
  root.innerHTML = `
    <div class="app">
      <header class="topnav">
        <a class="topnav-logo" href="#/projects" id="topnav-brand" aria-label="${esc(initialName)}">
          ${brandLockup({ branding: null, firmName, variant: 'nav', tagSuffix: 'Project Hub' })}
        </a>
        <div class="topnav-crumb" id="topnav-crumb"></div>
        <div class="topnav-actions">
          <button class="hamburger" id="sidebar-hamburger" aria-label="Open menu">
            <span></span><span></span><span></span>
          </button>
          <span class="user-email">${esc(userEmail)}</span>
          <button class="btn-link" id="signout-btn">Sign out</button>
        </div>
      </header>

      <aside class="app-sidebar" id="app-sidebar">
        <div class="sidebar-scroll" id="sidebar-scroll"></div>
        <button class="sidebar-collapse" id="sidebar-collapse" aria-expanded="true">
          <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M15 6l-6 6 6 6"/>
          </svg>
          <span class="sidebar-text">Collapse</span>
        </button>
      </aside>
      <div class="sidebar-backdrop" id="sidebar-backdrop"></div>

      <main class="app-main" id="app-main"></main>
    </div>
  `;

  const main = root.querySelector<HTMLElement>('#app-main')!;
  const sidebar = root.querySelector<HTMLElement>('#app-sidebar')!;
  const sidebarScroll = root.querySelector<HTMLElement>('#sidebar-scroll')!;
  const collapseBtn = root.querySelector<HTMLButtonElement>('#sidebar-collapse')!;
  const crumb = root.querySelector<HTMLElement>('#topnav-crumb')!;
  const backdrop = root.querySelector<HTMLElement>('#sidebar-backdrop')!;
  const hamburger = root.querySelector<HTMLButtonElement>('#sidebar-hamburger')!;

  hamburger.addEventListener('click', () => document.body.classList.add('sidebar-open'));
  backdrop.addEventListener('click', () => document.body.classList.remove('sidebar-open'));
  sidebar.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('a')) {
      document.body.classList.remove('sidebar-open');
    }
  });

  root.querySelector<HTMLButtonElement>('#signout-btn')!.addEventListener('click', () => {
    void signOut();
  });

  function renderGlobalSidebar(active: GlobalKey): string {
    const item = (key: GlobalKey, label: string, href: string): string =>
      `<a class="sidebar-item ${active === key ? 'is-active' : ''}" href="${href}" title="${esc(label)}">
         ${navIcon(key)}<span class="sidebar-text">${esc(label)}</span>
       </a>`;
    return `
      <div class="sidebar-section">
        <div class="sidebar-label">Workspace</div>
        ${item('projects', 'All Projects', '#/projects')}
        ${item('library', 'Material Library', '#/library')}
        ${item('contacts', 'Contacts', '#/contacts')}
        ${item('settings', 'Settings', '#/settings')}
      </div>
      <div class="sidebar-section">
        <div class="sidebar-label">Tools</div>
        <span class="sidebar-item is-disabled" title="Style Questionnaire">
          ${navIcon('questionnaire')}<span class="sidebar-text">Style Questionnaire</span>
        </span>
        <span class="sidebar-item is-disabled" title="Export &amp; Print">
          ${navIcon('export')}<span class="sidebar-text">Export &amp; Print</span>
        </span>
      </div>
    `;
  }

  function renderProjectSidebar(
    projectId: string, projectName: string, activeSection: ProjectSectionKey,
  ): string {
    const item = (key: ProjectSectionKey, label: string): string =>
      `<a class="sidebar-item ${activeSection === key ? 'is-active' : ''}"
          href="#/projects/${esc(projectId)}/${esc(key)}" title="${esc(label)}">
         ${navIcon(key)}<span class="sidebar-text">${esc(label)}</span>
       </a>`;
    return `
      <a class="sidebar-back" href="#/projects">← All projects</a>
      <div class="sidebar-project-name">${esc(projectName)}</div>
      <div class="sidebar-section">
        <div class="sidebar-label">Project Hub</div>
        ${PROJECT_HUB.map((s) => item(s.key, s.label)).join('')}
      </div>
      <div class="sidebar-section">
        <div class="sidebar-label">Tools</div>
        ${PROJECT_TOOLS.map((s) => item(s.key, s.label)).join('')}
      </div>
    `;
  }

  // Rail state is a per-browser convenience, so localStorage is the right
  // home for it — but it throws in a private window and can come back empty,
  // so every access is guarded and the expanded default always renders.
  const RAIL_KEY = 'tsd.nav.collapsed';
  const readRail = (): boolean => {
    try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
  };
  const applyRail = (collapsed: boolean): void => {
    document.body.classList.toggle('nav-collapsed', collapsed);
    collapseBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    collapseBtn.setAttribute('aria-label', collapsed ? 'Expand menu' : 'Collapse menu');
    collapseBtn.title = collapsed ? 'Expand menu' : 'Collapse menu';
  };
  applyRail(readRail());

  // Branding is cosmetic, so it loads after the shell is already on screen
  // rather than blocking it. A failure here leaves the built-in mark, which is
  // a perfectly good header — never an error state.
  void (async () => {
    try {
      const profile = await getMyProfile();
      if (!profile) return;
      // Re-render the whole lockup rather than patching pieces of it: the
      // photo, the mark, the name's typeface and its color all arrive at once,
      // and three separate queries into the DOM is how the Settings preview
      // drifted out of step with this header in the first place.
      const anchor = root.querySelector<HTMLElement>('#topnav-brand');
      if (anchor) {
        const logo = logoPath(profile);
        const photo = photoPath(profile);
        anchor.setAttribute('aria-label', companyName(profile, firmName));
        anchor.innerHTML = brandLockup({
          branding: profile, firmName, variant: 'nav',
          logoUrl: logo ? logoUrl(logo) : null,
          photoUrl: photo ? logoUrl(photo) : null,
          // The tagline is the firm's when they set one; ours only until then.
          tagSuffix: tagline(profile) ? null : 'Project Hub',
        });
      }
      ensureBrandFont(profile.brand_font);
    } catch { /* keep the built-in branding */ }
  })();
  collapseBtn.addEventListener('click', () => {
    const next = !document.body.classList.contains('nav-collapsed');
    applyRail(next);
    try { localStorage.setItem(RAIL_KEY, next ? '1' : '0'); } catch { /* private window */ }
  });

  function setContext(ctx: ShellContext): void {
    if (ctx.kind === 'global') {
      sidebarScroll.innerHTML = renderGlobalSidebar(ctx.active);
      crumb.textContent = '';
    } else {
      sidebarScroll.innerHTML = renderProjectSidebar(ctx.projectId, ctx.projectName, ctx.activeSection);
      const parts: string[] = [ctx.projectName];
      if (ctx.activeRoomName) parts.push(ctx.activeRoomName);
      crumb.innerHTML = parts.map((p, i) =>
        i === 0 ? `<span>${esc(p)}</span>` : `<span class="topnav-crumb-sep">›</span><span>${esc(p)}</span>`
      ).join('');
    }
  }

  setContext({ kind: 'global', active: 'projects' });

  return { main, setContext };
}
